import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { notify } from '../../services/notification';
import { X } from "lucide-react";
import { decode, encode } from "base-64";
import Cookies from "js-cookie";

import "./targetVsAchievement.scss";
import TargetVsAchievementTable from "./TargetVsAchievementTable";
import { constant } from "../../constants/constant";
import Header from "../../component/loaderComponent/Header/Header";
import { getTargetAchievementListAction, downloadTargetAchievementExcelAction, getAPSListAction, setSelectedAPSAction, clearSelectedAPSAction } from "../../actions/targetAchievementAction";
import { Image_Url } from "../../constants/imageUrl/ImageUrl";

export default function TargetVsAchievement() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const pageSize = constant?.paginationCount || 10;

  // Get persisted state from Redux
  const { selectedAPS, dataList, totalCount, currentPage: reduxCurrentPage } = useSelector(
    state => state.targetAchievement
  );

  const [currentPage, setCurrentPage] = useState(reduxCurrentPage || 1);
  const [searchField, setSearchField] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Role-based state
  const [userRole, setUserRole] = useState('');
  const [apsList, setApsList] = useState([]);

  const searchFieldOptions = [
    { value: "month", label: "Month" },
    { value: "year", label: "Year" },
  ];

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((totalCount || 0) / pageSize)),
    [totalCount, pageSize]
  );

  const pageWindow = useMemo(() => {
    const pages = [];
    const maxButtons = 7;

    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    const showLeftEllipsis = currentPage > 4;
    const showRightEllipsis = currentPage < totalPages - 3;

    pages.push(1);
    if (showLeftEllipsis) pages.push("left-ellipsis");

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    for (let i = start; i <= end; i++) pages.push(i);

    if (showRightEllipsis) pages.push("right-ellipsis");
    pages.push(totalPages);

    return pages;
  }, [currentPage, totalPages]);

  const fetchData = async (
    page = currentPage,
    field = searchField,
    value = searchValue,
    aps = selectedAPS
  ) => {
    try {
      setLoading(true);

      let queryParam = "";

      if (field && value.trim()) {
        queryParam += `&${field}=${value.trim()}`;
      }

      const res = await dispatch(
        getTargetAchievementListAction(page, pageSize, aps)
      );

      if (res?.status === 200) {
        const apiData = res?.data?.data || [];
        const count = res?.data?.count || 0;

        // Save to Redux for state persistence
        dispatch(setSelectedAPSAction(aps, apiData, count, page));

        const lastPage = Math.max(1, Math.ceil(count / pageSize));
        if (page > lastPage) setCurrentPage(lastPage);
      }
    } catch (error) {
      notify.error("Failed to load Plan vs Actual data");
    } finally {
      setLoading(false);
    }
  };

  const fetchAPSList = async () => {
    try {
      const res = await dispatch(getAPSListAction());
      if (res?.status === 200) {
        const apses = res?.data?.results?.data || [];
        setApsList(apses);
      }
    } catch (error) {
      notify.error("Failed to load APS list");
    }
  };

  const handleAPSChange = async (apsCode) => {
    if (apsCode) {
      // Fetch consolidated data for selected APS
      setCurrentPage(1);
      await fetchData(1, '', '', apsCode);
    } else {
      // Clear data from Redux
      dispatch(clearSelectedAPSAction());
      setCurrentPage(1);
    }
  };

  const handleViewDetail = (month) => {
    if (!selectedAPS) {
      notify.error("Please select an APS first");
      return;
    }

    // Encode parameters for URL
    const encodedAPS = encode(selectedAPS);
    const encodedMonth = encode(month);

    navigate(`/planvsactual/customer/${encodedAPS}/${encodedMonth}`);
  };

  useEffect(() => {
      fetchAPSList();
  }, []);

  useEffect(() => {
    if (selectedAPS) {
      fetchData(currentPage, searchField, searchValue, selectedAPS);
    }
  }, [currentPage]);

  // Sync local currentPage with Redux when coming back from navigation
  useEffect(() => {
    if (reduxCurrentPage && reduxCurrentPage !== currentPage) {
      setCurrentPage(reduxCurrentPage);
    }
  }, []);

  const handleSearch = () => {
    if (!searchField || !searchValue.trim()) return;

    const trimmed = searchValue.trim();

    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchData(1, searchField, trimmed, selectedAPS);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && searchField && searchValue.trim()) {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchField("");
    setSearchValue("");

    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchData(1, "", "", selectedAPS);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      setDownloading(true);
      let queryParam = "";

      if (searchField && searchValue.trim()) {
        queryParam += `${queryParam ? '&' : ''}${searchField}=${searchValue.trim()}`;
      }

      const fileBlob = await dispatch(downloadTargetAchievementExcelAction(queryParam, selectedAPS));

      if (fileBlob) {
        const blobUrl = URL.createObjectURL(fileBlob);
        const newWindow = window.open(blobUrl, '_blank');
        if (newWindow) {
          newWindow.focus();
        } else {
          notify.error('Popup blocked. Please allow popups for this website');
        }
      }
    } catch (error) {
      notify.error('Error downloading file');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="target-achievement-wrapper">
      <header className="target-achievement-header">
        <Header title={"Plan V/S Actual"} />
        <p className="target-achievement-subtitle">
          View monthly & YTD sales performance details for the retailers under your selected APS
        </p>
      </header>

      <section className="target-achievement-search-card">
        {/* APS Dropdown for Area Head and Super Admin */}
        {/* {(userRole === 'area_head' || userRole === 'super_admin') && ( */}
          <div className="aps-selection-row">
            <div>
              <label htmlFor="aps-select" className="aps-label">Select APS:</label>
              <select
                id="aps-select"
                className="target-achievement-dropdown aps-dropdown"
                value={selectedAPS}
                onChange={(e) => handleAPSChange(e.target.value)}
              >
                <option value=""> Select APS </option>
                {apsList.map((aps) => (
                  <option key={aps.id} value={aps.aps_code}>
                    {aps.aps_name}
                  </option>
                ))}
              </select>
            </div>
            {dataList.length > 0 && (
            <button
              className="target-achievement-download-btn"
              onClick={handleDownloadExcel}
              disabled={downloading || loading}
            >
              <img src={Image_Url.EXCEL_LOGO} className="excel-icon" alt="Excel" />
              {downloading ? 'Downloading...' : 'Download Excel'}
            </button>
            )}
          </div>
        {/* // )} */}

        {/* Show search and download only if APS is selected or user is not Area Head */}
        {/* {(selectedAPS || (userRole !== 'area_head' && userRole !== 'super_admin')) && (
          <div className="target-achievement-searchbar">
            <div className="target-achievement-search-controls">
              <select
                className="target-achievement-dropdown"
                value={searchField}
                onChange={(e) => {
                  setSearchField(e.target.value);
                  setSearchValue("");
                }}
              >
                <option value="">Select Field</option>
                {searchFieldOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              <div className="target-achievement-input-wrapper">
                <span className="icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="7" />
                    <path d="M21 21l-4.35-4.35" />
                  </svg>
                </span>
                <input
                  type="text"
                  placeholder={searchField ? `Enter ${searchFieldOptions.find(opt => opt.value === searchField)?.label}` : "Select a field first"}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={!searchField}
                />
                {(searchField || searchValue) && (
                  <button
                    className="target-achievement-clear-icon"
                    onClick={handleClearSearch}
                    disabled={loading}
                    aria-label="Clear search"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              <button
                className="target-achievement-search-btn"
                onClick={handleSearch}
                disabled={!searchField || !searchValue.trim() || loading}
              >
                Search
              </button>
            </div>

            <button
              className="target-achievement-download-btn"
              onClick={handleDownloadExcel}
              disabled={downloading || loading}
            >
              <img src={Image_Url.EXCEL_LOGO} className="excel-icon" alt="Excel" />
              {downloading ? 'Downloading...' : 'Download Excel'}
            </button>
          </div>
        )} */}
      </section>

      {/* Table View */}
      <TargetVsAchievementTable
        data={dataList}
        totalPages={totalPages}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        loading={loading}
        totalCount={totalCount}
        pageSize={pageSize}
        pageWindow={pageWindow}
        selectedAPS={selectedAPS}
        onViewDetail={handleViewDetail}
      />
    </div>
  );
}
